import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import i18n from '../../../../../../i18n';
import ConditionMatchersList from '../../../../../../components/transactionEditor/ConditionMatchersList';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Condition, Matcher } from '../../../../../../types/rest/dslTypes';
import clsx from 'clsx';
import {
  BlazeDropdownItem,
  DropdownPlacement,
} from '../../../../../../components/ui/dropdown/DropdownButton/DropdownButton';
import DropdownButton from '../../../../../../components/ui/dropdown/DropdownButton';

interface ProcessingActionConditionsProps {
  /** List of Conditions in current processing action */
  conditions: Condition[];
  onUpdateConditions: (conditions: Condition[]) => void;
  allConditions: Condition[];
}

const ProcessingActionConditions = (props: ProcessingActionConditionsProps) => {
  const { t } = useTranslation('transactions');
  const { conditions } = props;

  const addExistingCondition = (condition: Condition) => {
    const updated = _.isNil(props.conditions) ? [] : _.cloneDeep(props.conditions);
    updated.push(condition);
    props.onUpdateConditions(updated);
  };

  /**
   * Gets the dropdown with existing conditions.
   * It is a distinct list from all conditions across all processing actions,
   * which excludes conditions existing in the current action.
   * */
  const getAllAvailableConditions = (): BlazeDropdownItem[] => {
    const allConditions: BlazeDropdownItem[] = [];
    if (!_.isNil(props.allConditions)) {
      _.forEach(props.allConditions, (condition: Condition) => {
        //Check if this condition does not exist in current conditions
        if (_.findIndex(props.conditions, condition) === -1) {
          allConditions.push({
            content: `${condition.matcher.key} ${condition.matcher.matcherName} ${condition.matcher.matchingValue}`,
            fnCallback: () => {
              addExistingCondition(condition);
            },
          });
        }
      });
    }
    return allConditions;
  };

  const getDropdown = (dropDownPlacement: DropdownPlacement, items: BlazeDropdownItem[]) => {
    const isDisabled = _.size(items) === 0;
    return (
      <DropdownButton
        customToggle={getConditionToggle(isDisabled)}
        disabled={false}
        toggleTooltip={i18n.t('transactions:editor.selectCondition')}
        dropdownItems={items}
        inline={true}
        dropDownPlacement={dropDownPlacement}
      />
    );
  };

  const handleAddNew = () => {
    const emptyItem: Condition = {
      matcher: {
        matcherName: Matcher.equals,
        key: '',
        matchingValue: '',
      },
    };
    const list = conditions ? conditions.concat(emptyItem) : [emptyItem];
    props.onUpdateConditions(list);
  };

  const isEmpty = () => {
    return _.size(conditions) == 0;
  };

  const availableConditions = getAllAvailableConditions();

  return (
    <div className={'mt-5'}>
      <Row>
        <Col md={6}>
          <label className={'mb-1'} htmlFor="trigger-conditions-header">
            {i18n.t('transactions:processingActions.modal.conditionTitle')}
          </label>
        </Col>
        {!isEmpty() && (
          <Col md={6} className={'text-right'}>
            {getDropdown('RIGHT', availableConditions)}
          </Col>
        )}
      </Row>

      {isEmpty() ? (
        <div>
          {' '}
          <p>{i18n.t('transactions:processingActions.modal.triggerCondition')}</p>
          <div>
            <a
              href="#add-conditional-matcher"
              id={'qa-btn-add-first-conditional-matcher'}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleAddNew();
              }}
              className={'mr-2'}
              title={t('editor.addNewMatcher', { name: i18n.t('transactions:processingActions.modal.matcherTitle') })}
            >
              {t('editor.add')}
            </a>
            {_.size(availableConditions) !== 0 && (
              <>
                <span className={'mr-2'}>or</span>
                {getDropdown('LEFT', availableConditions)}
              </>
            )}
          </div>
        </div>
      ) : (
        <ConditionMatchersList
          isReadOnly={false}
          onListChange={props.onUpdateConditions}
          matcherList={conditions}
          handleAddNew={handleAddNew}
          id="trigger-conditions-header"
          matcherTitle={i18n.t('transactions:processingActions.modal.matcherTitle')}
          isWizardModal={false}
        />
      )}
      <hr className={'mt-4'} />
    </div>
  );
};

//TODO - use buildCustomToggle builder
const getConditionToggle = (disabled: boolean) =>
  // eslint-disable-next-line react/display-name
  React.forwardRef(({ onClick }: any, ref: any) => (
    <span
      onClick={e => {
        e.preventDefault();
        if (disabled) {
          return;
        }
        onClick(e);
      }}
      ref={ref}
      className="d-inline-block hand-cursor"
      title={disabled ? 'There are no further existing conditions available.' : undefined}
    >
      <a
        className={clsx('dropdown-toggle', disabled ? 'text-muted' : undefined)}
        role={'button'}
        style={{ textDecoration: 'none' }}
      >
        {i18n.t('transactions:editor.selectCondition')}
      </a>
    </span>
  ));

export default ProcessingActionConditions;
